// ═══════════════════════════════════════════════════════════════
// ChapterNav — Sidebar gauche liste chapitres (S56 P3)
// Badge statut coloré · + Ajouter · flèches up/down pour reorder V1
// (drag-drop reporté S57 polish).
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { Plus, ChevronUp, ChevronDown, Trash2, BookOpen, Sparkles, ArrowLeftRight } from 'lucide-react';
import { createChapter, reorderChapters, deleteChapter, updateChapter, updateBook } from '../../services/bookApi';
import { getTermsFromBook } from '../../utils/documentTerminology';
import AIImportModal from './AIImportModal';

const STATUS_DOT = {
    draft:        '#6a6a85',
    in_progress:  '#f59e0b',
    review:       '#3b82f6',
    validated:    '#10b981',
    consolidated: '#8b5cf6',
};

const ChapterNav = ({ book, projectId, chapters = [], currentChapterId, onSelectChapter, onChaptersChanged, onBookUpdated, onOpenTransition }) => {
    const [creating, setCreating] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [pending, setPending] = useState(false);
    const [showAIImport, setShowAIImport] = useState(false);
    const [hoverGap, setHoverGap] = useState(null); // index entre deux chapitres adjacents
    // Édition inline titres (livre + chapitres) — déclencheur double-clic
    const [editingId, setEditingId] = useState(null); // 'book' | chapterId | null
    const [editTitle, setEditTitle] = useState('');
    const [editPending, setEditPending] = useState(false);
    const t = getTermsFromBook(book);

    const startEdit = (id, currentTitle) => {
        setEditingId(id);
        setEditTitle(currentTitle || '');
    };
    const cancelEdit = () => {
        setEditingId(null);
        setEditTitle('');
    };
    const saveEdit = async () => {
        if (editPending || editingId === null) return;
        const nextTitle = editTitle.trim();
        if (!nextTitle) { cancelEdit(); return; }
        setEditPending(true);
        try {
            if (editingId === 'book') {
                if (book?.id && nextTitle !== book.title) {
                    await updateBook(book.id, { title: nextTitle });
                    if (onBookUpdated) onBookUpdated();
                }
            } else {
                const current = chapters.find(c => c.id === editingId);
                if (book?.id && editingId && nextTitle !== (current?.title || '')) {
                    await updateChapter(book.id, editingId, { metadata_updates: { title: nextTitle } });
                    if (onChaptersChanged) onChaptersChanged();
                }
            }
            cancelEdit();
        } catch {
            cancelEdit();
        } finally {
            setEditPending(false);
        }
    };

    const handleCreate = async () => {
        if (!book?.id || !newTitle.trim()) return;
        setPending(true);
        try {
            const result = await createChapter(book.id, { title: newTitle.trim() });
            setNewTitle('');
            setCreating(false);
            if (onChaptersChanged) onChaptersChanged();
            if (result?.chapter && onSelectChapter) onSelectChapter(result.chapter.id);
        } finally {
            setPending(false);
        }
    };

    const handleMove = async (chapterId, direction) => {
        const index = chapters.findIndex(c => c.id === chapterId);
        if (index < 0) return;
        const target = direction === 'up' ? index - 1 : index + 1;
        if (target < 0 || target >= chapters.length) return;
        const reordered = [...chapters];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        try {
            await reorderChapters(book.id, reordered.map(c => c.id));
            if (onChaptersChanged) onChaptersChanged();
        } catch {}
    };

    const handleDelete = async (chapterId) => {
        if (!window.confirm('Archiver ce chapitre ? Les versions seront conservées (soft delete).')) return;
        try {
            await deleteChapter(book.id, chapterId);
            if (onChaptersChanged) onChaptersChanged();
        } catch {}
    };

    return (
        <div style={{
            width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column',
            borderRight: '1px solid var(--card-border)',
            background: 'var(--card-bg-secondary)',
            height: '100%', minHeight: 0,
        }}>
            {/* Header livre */}
            <div style={{
                padding: '14px 16px',
                borderBottom: '1px solid var(--card-border)',
                display: 'flex', alignItems: 'center', gap: 10,
            }}>
                <div style={{
                    width: 28, height: 28, borderRadius: 6,
                    background: 'rgba(139,92,246,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <BookOpen size={14} style={{ color: '#a78bfa' }} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    {editingId === 'book' ? (
                        <input
                            autoFocus
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
                                else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                            }}
                            onBlur={saveEdit}
                            onClick={(e) => e.stopPropagation()}
                            disabled={editPending}
                            title="Enter pour valider, Escape pour annuler"
                            style={{
                                width: '100%', padding: '2px 6px',
                                fontSize: 13, fontWeight: 600, color: 'var(--text)',
                                background: 'rgba(139,92,246,0.08)',
                                border: '1px solid rgba(139,92,246,0.35)',
                                borderRadius: 4,
                                outline: 'none',
                                fontFamily: 'inherit',
                            }}
                        />
                    ) : (
                        <div
                            onDoubleClick={() => book?.id && startEdit('book', book.title)}
                            title={book?.id ? 'Double-clic pour renommer' : undefined}
                            style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: book?.id ? 'text' : 'default' }}
                        >
                            {book?.title || t.docLabel}
                        </div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {chapters.length} {chapters.length > 1 ? t.unitPlural.toLowerCase() : t.unitSingular.toLowerCase()}
                    </div>
                </div>
            </div>

            {/* Liste chapitres */}
            <div style={{ flex: 1, overflow: 'auto', padding: '8px 6px' }}>
                {chapters.length === 0 && (
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '16px 10px', textAlign: 'center' }}>
                        Pas encore de {t.unitSingular.toLowerCase()}.<br />Clique sur <strong>+ Ajouter</strong>.
                    </div>
                )}
                {chapters.map((c, i) => {
                    const selected = c.id === currentChapterId;
                    const dot = STATUS_DOT[c.status] || STATUS_DOT.draft;
                    const nextChapter = chapters[i + 1];
                    return (
                        <React.Fragment key={c.id}>
                        <div
                            onClick={() => onSelectChapter?.(c.id)}
                            style={{
                                padding: '8px 10px', margin: '2px 0',
                                borderRadius: 6, cursor: 'pointer',
                                background: selected ? 'rgba(139,92,246,0.12)' : 'transparent',
                                border: selected ? '1px solid rgba(139,92,246,0.25)' : '1px solid transparent',
                                display: 'flex', alignItems: 'center', gap: 8,
                            }}
                        >
                            <div style={{
                                width: 8, height: 8, borderRadius: 4,
                                background: dot, flexShrink: 0,
                            }} />
                            <div style={{ minWidth: 0, flex: 1 }}>
                                {editingId === c.id ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span style={{
                                            fontSize: 12, fontWeight: 500,
                                            color: 'var(--text-secondary)', flexShrink: 0,
                                        }}>{c.position}.</span>
                                        <input
                                            autoFocus
                                            value={editTitle}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
                                                else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                                            }}
                                            onBlur={saveEdit}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={editPending}
                                            title="Enter pour valider, Escape pour annuler"
                                            style={{
                                                flex: 1, minWidth: 0, padding: '1px 5px',
                                                fontSize: 12, fontWeight: 500, color: 'var(--text)',
                                                background: 'rgba(139,92,246,0.08)',
                                                border: '1px solid rgba(139,92,246,0.35)',
                                                borderRadius: 4,
                                                outline: 'none',
                                                fontFamily: 'inherit',
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        onDoubleClick={(e) => { e.stopPropagation(); startEdit(c.id, c.title); }}
                                        title="Double-clic pour renommer"
                                        style={{
                                            fontSize: 12, fontWeight: 500,
                                            color: selected ? 'var(--text)' : 'var(--text-secondary)',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {c.position}. {c.title || 'Sans titre'}
                                    </div>
                                )}
                                {(c.word_count ?? 0) > 0 && (
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                        {(c.word_count || 0).toLocaleString('fr-FR')} mots
                                    </div>
                                )}
                            </div>
                            {selected && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleMove(c.id, 'up'); }}
                                        disabled={i === 0}
                                        title="Monter"
                                        style={navBtn(i === 0)}
                                    ><ChevronUp size={10} /></button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleMove(c.id, 'down'); }}
                                        disabled={i === chapters.length - 1}
                                        title="Descendre"
                                        style={navBtn(i === chapters.length - 1)}
                                    ><ChevronDown size={10} /></button>
                                </div>
                            )}
                            {selected && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                                    title="Archiver"
                                    style={{
                                        padding: 4, borderRadius: 4, background: 'transparent',
                                        border: 'none', color: '#ef4444', cursor: 'pointer',
                                    }}
                                ><Trash2 size={11} /></button>
                            )}
                        </div>
                        {/* S58 — Zone de transition entre deux chapitres adjacents */}
                        {nextChapter && onOpenTransition && (
                            <div
                                onMouseEnter={() => setHoverGap(i)}
                                onMouseLeave={() => setHoverGap(null)}
                                onClick={(e) => { e.stopPropagation(); onOpenTransition(c.id, nextChapter.id); }}
                                title={`Soigner la transition ${c.position} → ${nextChapter.position}`}
                                style={{
                                    height: hoverGap === i ? 22 : 6,
                                    margin: '0 10px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'height 0.18s ease',
                                    color: hoverGap === i ? '#a78bfa' : 'transparent',
                                    borderTop: hoverGap === i ? '1px dashed rgba(139,92,246,0.35)' : '1px dashed transparent',
                                    borderBottom: hoverGap === i ? '1px dashed rgba(139,92,246,0.35)' : '1px dashed transparent',
                                    background: hoverGap === i ? 'rgba(139,92,246,0.05)' : 'transparent',
                                    borderRadius: 4,
                                }}
                            >
                                {hoverGap === i && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 4,
                                        fontSize: 10, fontWeight: 500,
                                    }}>
                                        <ArrowLeftRight size={10} />
                                        Transition
                                    </div>
                                )}
                            </div>
                        )}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* Ajouter */}
            <div style={{ padding: 10, borderTop: '1px solid var(--card-border)' }}>
                {creating ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <input
                            autoFocus
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleCreate();
                                else if (e.key === 'Escape') { setCreating(false); setNewTitle(''); }
                            }}
                            placeholder={`Titre ${t.docArticle === 'une' ? 'de la' : 'du'} ${t.unitSingular.toLowerCase()}`}
                            style={{
                                width: '100%', padding: '6px 10px',
                                borderRadius: 6, fontSize: 12,
                                background: 'rgba(255,255,255,0.04)',
                                border: '1px solid rgba(139,92,246,0.3)',
                                color: 'var(--text)',
                            }}
                        />
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button
                                onClick={handleCreate}
                                disabled={pending || !newTitle.trim()}
                                style={{
                                    flex: 1, padding: '6px 10px', borderRadius: 6,
                                    background: 'rgba(139,92,246,0.2)',
                                    border: '1px solid rgba(139,92,246,0.4)',
                                    color: '#a78bfa', fontSize: 11, cursor: 'pointer',
                                    opacity: pending ? 0.5 : 1,
                                }}
                            >Créer</button>
                            <button
                                onClick={() => { setCreating(false); setNewTitle(''); }}
                                style={{
                                    padding: '6px 10px', borderRadius: 6,
                                    background: 'transparent',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer',
                                }}
                            >Annuler</button>
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <button
                            onClick={() => setCreating(true)}
                            style={{
                                width: '100%', padding: '8px 10px', borderRadius: 6,
                                background: 'rgba(139,92,246,0.08)',
                                border: '1px dashed rgba(139,92,246,0.25)',
                                color: '#a78bfa', fontSize: 12, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            }}
                        >
                            <Plus size={12} /> Ajouter {t.docArticle === 'une' ? 'une' : 'un'} {t.unitSingular.toLowerCase()}
                        </button>
                        <button
                            onClick={() => setShowAIImport(true)}
                            title={`Laisser l'IA classer les fichiers du projet en structure ${t.unitPlural.toLowerCase()}`}
                            style={{
                                width: '100%', padding: '7px 10px', borderRadius: 6,
                                background: 'rgba(139,92,246,0.04)',
                                border: '1px solid rgba(139,92,246,0.15)',
                                color: '#a78bfa', fontSize: 11, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            }}
                        >
                            <Sparkles size={11} /> Importer & classer via IA
                        </button>
                    </div>
                )}
            </div>

            {showAIImport && book && projectId && (
                <AIImportModal
                    bookId={book.id}
                    projectId={projectId}
                    book={book}
                    onClose={() => setShowAIImport(false)}
                    onCompleted={() => {
                        setShowAIImport(false);
                        if (onChaptersChanged) onChaptersChanged();
                    }}
                />
            )}
        </div>
    );
};

const navBtn = (disabled) => ({
    padding: 2, borderRadius: 3, background: 'rgba(255,255,255,0.04)',
    border: 'none', color: disabled ? 'var(--text-muted)' : 'var(--text-secondary)',
    cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex',
});

export default ChapterNav;
